import React, { createContext, useContext, useState, useEffect } from "react";
import { useWebSocket } from "./WebSocketProvider";
import { DataState } from "./DataProvider";

const TaskPlanContext = createContext(null);

/**
 * Provides the state for the current task's plan, updated in real-time via WebSockets.
 */
export const TaskPlanProvider = ({ children }) => {
  const { subscribe, isConnected } = useWebSocket();
// Get the initial plan from the main context and alias it to avoid name conflicts.
  const { currentTaskId, currentTaskPlan: plan, setCurrentTaskPlan: setPlan,currentToolCallStatus,setCurrentToolCallStatus } = DataState(); 
  const [planStatus, setPlanStatus] = useState("idle"); // idle, planning, analyzing


  // Clear the plan whenever the user starts a new task (currentTaskId changes)
  useEffect(() => {
    setPlan([]);
    setPlanStatus("idle");
  }, [currentTaskId]);

  // Subscribe to plan notifications from the WebSocket server
  useEffect(() => {
    // Ensure we have a connection and an active task before subscribing
    if (!isConnected || !currentTaskId) {
      return;
    }

    const handlePlanNotification = (notification) => {
      // Only update the UI if the notification is for the currently active task
      console.log("Received plan notification:", notification);
      if (notification.task_id === currentTaskId) {
        if (notification.event_type === "plan_status") { 
          // Update the plan status based on the notification
          setPlanStatus(notification.status || "idle");
          return; // Exit early if this is just a status update
        } else {
          setPlan(notification.plan || []);
          setPlanStatus("idle"); // Reset status to idle after updating the plan
        }

      }
    };

    // --- NEW: Handler for the tool call analysis status event ---
    const handleToolCallAnalysisStatus = (notification) => {
        // Only update if the notification is for the currently active task
        if (notification.task_id !== currentTaskId) return;

        console.log("Received tool call analysis status:", notification);
        const { tool_call_id, status } = notification;

        // Update the specific tool call's running_phase
        setCurrentToolCallStatus(prevStatus => 
            prevStatus.map(toolCall => {
                if (toolCall.id === tool_call_id) {
                    // Return a new object for the matched tool call with the updated phase
                    return { ...toolCall, running_phase: status };
                }
                // Return the unchanged tool call if it's not the one we're looking for
                return toolCall;
            })
        );
    };

    const unsubscribe = subscribe({
      'task_plan_notification': handlePlanNotification,
      'task_tool_call_analysis_status': handleToolCallAnalysisStatus, // Add the new subscription
    });

    // Clean up the subscription when the component unmounts or dependencies change
    return () => {
      unsubscribe();
    };
  }, [subscribe, isConnected, currentTaskId]);

  return (
    <TaskPlanContext.Provider value={{ plan, planStatus }}>
      {children}
    </TaskPlanContext.Provider>
  );
};

// Custom hook for easy consumption of the TaskPlanContext
export const useTaskPlan = () => useContext(TaskPlanContext);